import { sandboxState } from './sandbox'

/**
 * Un secteur, tel que la roue le lit. Deux sources, même forme :
 *
 * - la DOTATION d'une soirée (`game.wheel.entries`, depuis le 19/09) : ce que
 *   fait chaque secteur, le lot réel, sa chance « 1 sur N » ;
 * - les options d'un ancien modèle (`game.luckyWheel.options`), sans chance.
 */
type Entry = {
    kind: 'gain' | 'replay' | 'lost'
    label?: string
    label_en?: string
    sectors?: number[]
    /** « 1 sur N ». Absente : l'issue ne se tire pas, elle s'impose seulement. */
    chance?: number
}

/** Le nombre de secteurs des visuels de roue. */
const SECTORS = 8

const pick = <T,>(items: T[]): T | undefined => (items.length ? items[Math.floor(Math.random() * items.length)] : undefined)

const sectorsOf = (entries: Entry[], kind: Entry['kind']) =>
    entries.filter((entry) => entry.kind === kind).flatMap((entry) => entry.sectors ?? [])

/**
 * Le tirage du serveur (`drawInOrder`), refait ici : du plus RARE au plus
 * courant, chacun testé « 1 sur N » ; le premier qui sort l'emporte, et rien
 * qui sort, c'est perdu. Sans stock : l'aperçu ne distribue rien.
 */
const drawLikeTheServer = (entries: Entry[]): Entry | undefined =>
    entries
        .filter((entry) => entry.kind !== 'lost' && Number.isFinite(entry.chance) && (entry.chance as number) >= 1)
        .map((entry) => ({ entry, tie: Math.random() }))
        .sort((a, b) => (b.entry.chance as number) - (a.entry.chance as number) || a.tie - b.tie)
        .find(({ entry }) => Math.random() < 1 / (entry.chance as number))?.entry

/**
 * Un tour de roue SIMULÉ, à la forme de `POST …/lucky-wheel/ranking`.
 *
 * **La dotation d'abord** (23/09) : fourni par le CMS, le réglage de la soirée
 * décide — mêmes secteurs, mêmes chances, même tirage que le serveur. C'est
 * ce qui manquait : depuis que la roue se règle sur la soirée, l'aperçu lisait
 * encore les options du modèle, vides — il tombait au hasard, n'y trouvait
 * rien, et annonçait « perdu » jusque sur « +1 tour ».
 *
 * Une issue imposée par le CMS l'emporte sur le hasard : c'est ce qui permet
 * de voir chaque modale sans attendre la chance.
 */
export const simulateWheelRound = (lang: string) => {
    const game = sandboxState.game ?? {}
    const dotation: Entry[] = game.wheel?.entries ?? []
    const entries: Entry[] = dotation.length ? dotation : game.luckyWheel?.options ?? []
    const settings = sandboxState.simulation.wheel ?? {}
    const outcome = settings.outcome ?? 'random'
    const labelOf = (entry?: Entry) => (entry ? (lang === 'en' && entry.label_en) || entry.label : undefined)

    let entry: Entry | undefined

    if (outcome === 'random') {
        if (dotation.length) {
            entry = drawLikeTheServer(dotation)
        } else {
            // Un ancien modèle, sans chances : la roue s'arrête au hasard et dit
            // ce que porte le secteur — perdu, puis rejouer, puis un des lots.
            const sector = 1 + Math.floor(Math.random() * SECTORS)
            const holders = entries.filter((e) => e.sectors?.includes(sector))
            entry = holders.find((e) => e.kind === 'lost') ?? holders.find((e) => e.kind === 'replay') ?? pick(holders.filter((e) => e.kind === 'gain'))
        }
    } else if (outcome === 'lost' || outcome === 'replay') {
        entry = entries.find((e) => e.kind === outcome)
    } else {
        entry = entries[Number(outcome.split(':')[1])]
    }

    const kind: Entry['kind'] = entry?.kind === 'gain' || entry?.kind === 'replay' ? entry.kind : 'lost'
    // Perdu : un secteur « perdu » ; sinon, un des secteurs de ce qui est sorti.
    const sector = (kind === 'lost' ? pick(sectorsOf(entries, 'lost')) : pick(entry?.sectors ?? [])) ?? 1

    sandboxState.roundsLeft = Math.max(0, sandboxState.roundsLeft - 1)
    if (kind === 'replay') sandboxState.roundsLeft += 1
    if (kind === 'gain') sandboxState.won = true

    const consolation =
        sandboxState.roundsLeft === 0 && !sandboxState.won && settings.consolation
            ? { _id: 'preview-consolation', title: settings.consolation }
            : undefined

    return {
        status: kind === 'lost' ? 'lost' : 'won',
        roundsCount: sandboxState.roundsLeft,
        ...(kind === 'gain' ? { gainType: 'gainOther', gain: { _id: 'preview-gain', title: labelOf(entry) ?? '' } } : {}),
        ...(kind === 'replay' ? { gainType: 'roundPlus1' } : {}),
        sector,
        ...(kind !== 'lost' && entry ? { label: labelOf(entry) } : {}),
        ...(consolation ? { consolation } : {}),
        test: true
    }
}
